'use client';

import { useEffect, useRef } from 'react';

import { getBoundHostOrigin, setBoundHostOrigin } from '~/utils/hostOrigin';
import { type PluginHostMessage } from '~/utils/pluginHostMessaging';

/**
 * Standard URL parameter name for the host origin.
 * Set by the plugin when building the iframe URL.
 */
const HOST_ORIGIN_PARAM = 'hostOrigin';

/**
 * Reads the hostOrigin URL param set by the plugin when building the iframe URL.
 * This origin was already validated by the middleware CSP before the page loaded.
 */
function getHostOriginFromUrl(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get(HOST_ORIGIN_PARAM);
}

/**
 * Validates whether a message origin should be trusted.
 *
 * Before Init handshake: accepts plugin-specific trusted origins, the hostOrigin from URL params
 * (already validated by middleware CSP), and localhost in dev.
 *
 * After Init handshake: only accepts the bound host origin.
 */
function isAllowedOrigin(origin: string, isTrustedOrigin: (origin: string) => boolean): boolean {
  // After handshake, only accept from the bound origin
  const bound = getBoundHostOrigin();
  if (bound) {
    return origin === bound;
  }

  // Before handshake: accept plugin-specific trusted domains
  if (isTrustedOrigin(origin)) {
    return true;
  }

  // Accept the hostOrigin from URL params (set by plugin, validated by middleware CSP)
  const hostOrigin = getHostOriginFromUrl();
  if (hostOrigin && origin === hostOrigin) {
    return true;
  }

  // Allow localhost in development
  if (process.env.NODE_ENV === 'development') {
    try {
      return new URL(origin).hostname === 'localhost';
    } catch {
      return false;
    }
  }

  return false;
}

/**
 * Configuration for the plugin host listener hook.
 */
export type PluginHostListenerConfig<TMessage extends PluginHostMessage> = {
  /** Type guard to validate incoming messages from the host */
  isHostMessage: (data: unknown) => data is TMessage;
  /** Check if an origin should be trusted before the Init handshake (e.g., *.sanity.studio) */
  isTrustedOrigin: (origin: string) => boolean;
  /** The event type string that triggers origin binding (e.g., 'sanity-to-air:init') */
  initEventType: string;
  /** Callback when a valid message is received */
  onMessage: (message: TMessage) => void;
};

/**
 * Generic hook to listen for messages from a plugin host.
 *
 * Implements a two-phase origin validation:
 * 1. Before Init: accepts plugin-specific trusted origins and the hostOrigin URL param
 * 2. After Init: binds to the actual event.origin and only accepts from that origin
 */
export function usePluginHostListener<TMessage extends PluginHostMessage>(config: PluginHostListenerConfig<TMessage>) {
  const configRef = useRef(config);

  useEffect(() => {
    configRef.current = config;
  });

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from the parent window (the plugin host)
      if (event.source !== window.parent) {
        return;
      }

      const { initEventType, isHostMessage, isTrustedOrigin, onMessage } = configRef.current;

      if (!isAllowedOrigin(event.origin, isTrustedOrigin)) {
        return;
      }

      if (!isHostMessage(event.data)) {
        return;
      }

      // Bind host origin on Init handshake
      if (event.data.type === initEventType) {
        setBoundHostOrigin(event.origin);
      }

      onMessage(event.data);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
}
